import { LoadingButton } from "@mui/lab";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import {
  Typography,
  Button,
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  Link,
  Switch,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Tooltip,
  Radio,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import axios from "axios";
import { useState, useEffect } from "react";
import { updateCityConnects } from "../../../store/actions/userActions";
import { updateSubCityConnects } from "../../../store/actions/subUserActions";

const SetupJaxDetails = ({
  connectToCity,
  setConnectToCity,
  selectedCity,
  county,
  selectedCityUser,
  setSelectedCityUser,
  addNewUser,
  setAddNewUser,
  selectUserLoading,
  setSelectUserLoading,
  selectedForms,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);
  const dispatch = useDispatch();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [usernameError, setUsernameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [warningText, setWarningText] = useState(
    "Your credentials are securely encrypted and remain inaccessible to anyone. We prioritize your privacy and use top-tier data security measures to protect your information.",
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5); // Track which row is loading by ID
  const [selectUserInfo, setSelectUserInfo] = useState(null); // { type: 'success' | 'error', message: string }
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null); // { id, user_name }

  useEffect(() => {
    if (connectToCity && !selectedForms?.includes("noc")) {
      setConnectToCity(false);
    }
  }, [connectToCity, selectedForms, setConnectToCity]);

  const handleSelectUser = async (row) => {
    if (selectedCityUser === row.id) {
      setSelectedCityUser("");
      setSelectUserInfo(null);
      setIsError(false);
      return;
    }

    // Validate credentials when selecting a user
    setSelectUserInfo(null);
    setIsError(false);
    setSelectUserLoading(row.id);

    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/noc/validate-city-credentials`,
        {
          currentSession: currentSession,
          city_user_id: row.id,
          county: county,
          city: selectedCity,
        },
        { withCredentials: true },
      );

      if (resp?.data?.success) {
        setSelectedCityUser(row.id);
        setSelectUserInfo({
          type: "success",
          message: "Credentials validated successfully!",
        });
        setIsError(false);
      } else {
        setSelectedCityUser("");
        const errorMessage =
          resp?.data?.message ||
          "Invalid credentials! The password may have been changed.";
        setSelectUserInfo({
          type: "error",
          message: `${errorMessage} Please remove this user (using the delete icon) and add new credentials.`,
        });
        setIsError(true);
      }
      setSelectUserLoading(null);
    } catch (e) {
      setSelectedCityUser("");
      const errorMessage =
        e?.response?.data?.message ||
        "Invalid credentials! The password may have been changed.";
      setSelectUserInfo({
        type: "error",
        message: `${errorMessage} Please remove this user (using the delete icon) and add new credentials.`,
      });
      setIsError(true);
      setSelectUserLoading(null);
    }
  };

  // Clear success message after 5 seconds, error messages after 10 seconds
  useEffect(() => {
    if (selectUserInfo) {
      const timeout = selectUserInfo.type === "success" ? 5000 : 10000;
      const timer = setTimeout(() => {
        setSelectUserInfo(null);
      }, timeout);
      return () => clearTimeout(timer);
    }
  }, [selectUserInfo]);

  const handleRemoveUserClick = (row) => {
    // Find the user name from the row
    const user = currentUser?.city_connects?.[county.toLowerCase()]?.[
      selectedCity.toLowerCase()
    ]?.find((u) => u.id === row);

    setUserToDelete({ id: row, user_name: user?.user_name || "this user" });
    setDeleteModalOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setUserToDelete(null);
  };

  const handleRemoveUser = async () => {
    if (!userToDelete) return;

    setWarningText("");
    setSelectUserInfo(null);
    setDeleteModalOpen(false);

    try {
      setCityLoading(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/noc/remove-city-credential`,
        {
          currentSession: currentSession,
          county: county,
          city: selectedCity,
          id: userToDelete.id,
        },
        { withCredentials: true },
      );

      if (resp?.data?.success) {
        // await dispatch(updatePermitPackage(resp?.data?.data));
        if (currentSession === "userSession") {
          await dispatch(updateCityConnects(resp?.data?.data));
        } else {
          await dispatch(updateSubCityConnects(resp?.data?.data));
        }
        setSelectedCityUser("");
        setUsername("");
        setPassword("");
        setAgreeToTerms(false);
        setUsernameError(false);
        setPasswordError(false);
        setIsError(false);
        setWarningText(
          "Your credentials are securely encrypted and cannot be viewed or decrypted by anyone. We use the highest standards of data security to safeguard your information.",
        );
      }
      setAddNewUser(false);
      setCityLoading(false);
      setUserToDelete(null);
    } catch (e) {
      setWarningText(
        "Login failed. Please check your credentials and try again.",
      );
      setIsError(true);
      setUsernameError(true);
      setPasswordError(true);
      setCityLoading(false);
      setUserToDelete(null);
    }
  };

  const handleAcceptCityScrape = async () => {
    setWarningText("");
    try {
      setCityLoading(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/noc/save-city_credentials`,
        {
          currentSession: currentSession,
          username: username,
          password: password,
          county: county,
          city: selectedCity,
        },
        { withCredentials: true },
      );

      if (resp?.data?.success) {
        // await dispatch(updatePermitPackage(resp?.data?.data));
        if (currentSession === "userSession") {
          await dispatch(updateCityConnects(resp?.data?.data));
        } else {
          await dispatch(updateSubCityConnects(resp?.data?.data));
        }
        setUsername("");
        setPassword("");
        setAgreeToTerms(false);
        setUsernameError(false);
        setPasswordError(false);
        setIsError(false);
        setWarningText(
          "Your credentials are securely encrypted and cannot be viewed or decrypted by anyone. We use the highest standards of data security to safeguard your information.",
        );
      }
      setAddNewUser(false);
      setCityLoading(false);
    } catch (e) {
      setWarningText(
        "Login failed. Please check your credentials and try again.",
      );
      setIsError(true);
      setUsernameError(true);
      setPasswordError(true);
      setCityLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          height: "calc(100vh - 200px)",
          overflowY: "auto",
        }}
      >
        <Typography
          variant="h5"
          mt={2}
          align="center"
          color="primary"
          gutterBottom
        >
          Automate Your Permit Process with the City of Jacksonville
        </Typography>

        <Typography
          variant="body2"
          fontWeight="bold"
          align="center"
          px={2}
          mb={2}
        >
          To save you time and reduce errors, Permit Rockstar offers full
          automation with the City of Jacksonville’s permit portal. If you
          enable this option, we’ll generate the permit number for you, pre-fill
          city forms using your selected data, and upload all required
          documents. This means you only need to review and submit. It’s fast,
          reliable, and completely secure.
        </Typography>

        <Box display="flex" justifyContent="center">
          <FormControlLabel
            control={
              <Switch
                disabled={!selectedForms?.includes("noc")}
                checked={connectToCity}
                onChange={(e) => {
                  if (!e.target.checked) {
                    setUsername("");
                    setPassword("");
                    setAgreeToTerms(false);
                    setUsernameError(false);
                    setPasswordError(false);
                    setIsError(false);
                    setWarningText(
                      "Your credentials are securely encrypted and cannot be viewed or decrypted by anyone. We use the highest standards of data security to safeguard your information.",
                    );
                    setAddNewUser(false);
                    setSelectedCityUser("");
                    setSelectUserInfo(null);
                    setSelectUserLoading(null);
                  }
                  setConnectToCity(e.target.checked);
                }}
              />
            }
            label="Enable Automation for This Permit Package"
          />
        </Box>

        {/* Conditionally render based on the automation switch */}
        {connectToCity ? (
          // If user already gave consent before
          currentUser?.permit_consent?.[county.toLowerCase()]?.[
            selectedCity.toLowerCase()
          ]?.consent === true ? (
            <Box sx={{ mt: 4 }}>
              {/* You can render a mini table or a summary of saved city creds */}
              <Typography variant="h6" align="center" color="success.main">
                Your login credentials are already saved for this city.
              </Typography>
              <Typography
                align="center"
                fontWeight="bold"
                variant="body2"
                mt={0}
                mb={3}
              >
                If you’d like to use a different account, you can remove it
                below and enter new login details.
              </Typography>
              {/* Replace with actual component later */}
              <>[City Credentials Table / Remove Button / Replace Form]</>
            </Box>
          ) : (
            <Box mt={3}>
              {/* Login form when no consent yet */}
              <Typography variant="h6" align="center" color="primary">
                Enter Your City Website Login
              </Typography>
              <Typography align="center" variant="body2" mt={1} mb={2}>
                We'll use these credentials to log into the Jacksonville permit
                portal on your behalf and pre-fill everything for you.
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button
                  color={addNewUser ? "error" : "primary"}
                  variant="text"
                  sx={{ textDecoration: "underline", whiteSpace: "nowrap" }}
                  onClick={() => {
                    setUsername("");
                    setPassword("");
                    setAgreeToTerms(false);
                    setUsernameError(false);
                    setPasswordError(false);
                    setIsError(false);
                    setWarningText(
                      "Your credentials are securely encrypted and cannot be viewed or decrypted by anyone. We use the highest standards of data security to safeguard your information.",
                    );
                    setSelectedCityUser("");
                    setSelectUserInfo(null);

                    setAddNewUser(!addNewUser);
                  }}
                >
                  {!addNewUser ? "Add New User" : "Cancel"}
                </Button>
                {selectUserInfo && (
                  <Typography
                    variant="body2"
                    sx={{
                      pl: 2,
                      color:
                        selectUserInfo.type === "success"
                          ? "success.main"
                          : "error.main",
                      fontWeight: "bold",
                    }}
                  >
                    {selectUserInfo.message}
                  </Typography>
                )}
              </Box>
              {!addNewUser ? (
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>City Portal Role</TableCell>
                        <TableCell>Added At</TableCell>
                        <TableCell align="right">Select</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentUser?.city_connects?.[county.toLowerCase()]?.[
                        selectedCity.toLowerCase()
                      ]
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage,
                        )
                        .map((row) => (
                          <TableRow key={row.id}>
                            <TableCell sx={{ pl: 0, py: 0.5 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <Tooltip title="Remove User">
                                  <IconButton
                                    color="error"
                                    onClick={() =>
                                      handleRemoveUserClick(row.id)
                                    }
                                    aria-label="delete"
                                    disabled={
                                      cityLoading || selectUserLoading !== null
                                    }
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>

                                <Typography variant="body2">
                                  {row.user_name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {row?.role
                                ?.replace(/_/g, " ")
                                .replace(/\b\w/g, (l) => l.toUpperCase())}
                            </TableCell>
                            <TableCell sx={{ pl: 0, py: 0.5 }}>
                              {DateTime.fromISO(row.date, { zone: "utc" })
                                .setZone("America/New_York")
                                .toFormat("MM/dd/yyyy")}
                            </TableCell>
                            <TableCell sx={{ pl: 0, py: 0.5 }} align="right">
                              {selectUserLoading === row.id ? (
                                <CircularProgress
                                  size={20}
                                  className="rotate"
                                />
                              ) : (
                                <Radio
                                  checked={selectedCityUser === row.id}
                                  onChange={() => handleSelectUser(row)}
                                  value={row.id}
                                  disabled={selectUserLoading !== null}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={
                      currentUser?.city_connects?.[county.toLowerCase()]?.[
                        selectedCity.toLowerCase()
                      ].length || 0
                    }
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                  />
                </TableContainer>
              ) : (
                <>
                  <Box component="form" sx={{ mt: 3, px: 0.5 }}>
                    <TextField
                      error={usernameError}
                      helperText={
                        usernameError ? "Please enter a valid username" : ""
                      }
                      disabled={cityLoading || selectUserLoading !== null}
                      label="City Website Username"
                      variant="filled"
                      fullWidth
                      value={username}
                      onChange={(e) => {
                        setUsername(e.target.value);
                        if (usernameError) setUsernameError(false);
                        if (passwordError) setPasswordError(false);
                        if (
                          warningText !== "" &&
                          warningText ===
                            "Login failed. Please check your credentials and try again."
                        )
                          setWarningText("");
                        if (isError) setIsError(false);
                      }}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      error={passwordError}
                      helperText={
                        passwordError ? "Please enter a valid password" : ""
                      }
                      disabled={cityLoading || selectUserLoading !== null}
                      label="City Website Password"
                      type="password"
                      variant="filled"
                      fullWidth
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (passwordError) setPasswordError(false);
                        if (usernameError) setUsernameError(false);
                        if (
                          warningText !== "" &&
                          warningText ===
                            "Login failed. Please check your credentials and try again."
                        )
                          setWarningText("");
                        if (isError) setIsError(false);
                      }}
                      sx={{ mb: 2 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={agreeToTerms}
                          onChange={(e) => setAgreeToTerms(e.target.checked)}
                          name="agreeToTerms"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          I agree to the{" "}
                          <Link
                            href="https://permitrockstar.com//terms-and-conditions/"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Terms and Conditions
                          </Link>
                        </Typography>
                      }
                    />

                    <Typography
                      align="center"
                      color="error"
                      variant="body2"
                      sx={{ mt: 2, fontWeight: "bold" }}
                    >
                      {warningText}
                    </Typography>
                  </Box>

                  <Box mt={3} sx={{ display: "flex", flexDirection: "column" }}>
                    <LoadingButton
                      disabled={
                        !username ||
                        !password ||
                        !agreeToTerms ||
                        isError ||
                        selectUserLoading !== null
                      }
                      loading={cityLoading}
                      color="success"
                      onClick={handleAcceptCityScrape}
                      variant="contained"
                      fullWidth
                    >
                      Provide Consent
                    </LoadingButton>
                  </Box>
                </>
              )}
            </Box>
          )
        ) : (
          <Typography
            align="center"
            color={!selectedForms?.includes("noc") ? "error" : "text.secondary"}
            mt={3}
          >
            {!selectedForms?.includes("noc")
              ? "Notice of Commencement must be selected to enable automation for this permit package."
              : "You chose to skip automation for this permit package. You can proceed manually."}
          </Typography>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Remove User</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to remove user:{" "}
            <strong>{userToDelete?.user_name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. You will need to add the credentials
            again if you want to use this user.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            disabled={cityLoading}
            variant="outlined"
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleRemoveUser}
            loading={cityLoading}
            variant="contained"
            color="error"
          >
            Remove
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SetupJaxDetails;
